import React, { FunctionComponent, ReactElement, useState } from "react";
import { Node, Edge, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { BannerStatus, Button, Form } from "@plex/react-components";
import {
  IActionEventPropertyOptions,
  IDataParam,
  IDataTypeOptions,
  IEventPropertyOptions,
  IFlowInputEditorOptions,
  INodeProperty,
  ISourceApiPickerOptions,
  IStandardObjectPickerOptions,
  NodeType
} from "../NodeTypes/Base/NodeTypeDefinitions";
import { TextFormField } from "./TextFormField";
import { TemplateEditorField } from "./TemplateEditorField";
import { StopTypeFormField } from "./StopTypeFormField";
import { FlowOutputFormSection } from "./FlowOutputFormSection";
import { ValueFormField } from "./ValueFormField";
import { EnableOutputsFormSection } from "./EnableOutputsFormSection";
import { DataSourcePickerFormField } from "./DataSourcePickerFormField";
import { EnableInputsFormSection } from "./EnableInputsFormSection";
import { FlowInputFormSection } from "./FlowInputFormSection";
import { ItemPositionFormField } from "./ItemPositionFormField";
import { EnumFormField } from "./EnumFormField";
import { DataTypeFormField } from "./DataTypeFormField";
import { camelCaseToReadable, forceNodeUpdate } from "../Util/NodeUtil";
import { CheckboxFormField } from "./CheckboxFormField";
import { NumericFormField } from "./NumericFormField";
import { primitiveTypesSelect, SourceAPI, IActionEventPickerRow, IExternalApi } from "../NodeTypes/TypeDefinitions";
import { DataInputFormField } from "./DataInputFormField";
import { NodeConfigPropertyType } from "../FlowDocument/PropertyTypeDefinitions";
import { DataPropertyCountFormField } from "../NodePropertiesForm/DataPropertyCountFormField";
import { ViewDataSourceDetailsField } from "./ViewDataSourceDetailsField";
import { ViewPlexAgentDetailsButton } from "./ViewPlexAgentDetailsButton";
import { SourceApiPickerFormField } from "./SourceApiPickerFormField";
import {
  getFlowInputName,
  INodeMetadata,
  loadNodeMetadata,
  resetIo,
  setFlowInputsToEventSchemas
} from "../NodeTypes/SchemaSystem";
import { useFunctionSubscriber } from "../FunctionSubscriberContext/FunctionSubscriberContext";
import { checkAndRemoveUnmappable, getNameFromDataHandleId, INewEdgeSchema } from "../Util/EdgeUtil";
import { addSchemas, IDesignerSchema } from "../NodeTypes/DataSchemas";
import { convertKeysToCamelCaseArray } from "../Util/KeyFormatter";
import { IDocumentDataOutput, IDocumentSchema } from "../FlowDocument/FlowDocumentModel";
import { camelToUpperSnakeCase } from "../FlowDocument/DocumentProcessor";
import { SchemaPickerField } from "./SchemaPickerField";
import { SchemaPropertyPickerField } from "./SchemaPropertyPickerField";
import { useViewController } from "../ViewContext";
import { LoopVariableEditor } from "../UserDefinedSchemaManagement/LoopVariableEditor";

export interface IBaseNodeProperties {
  id: string;
}

export const BaseNodePropertiesForm: FunctionComponent<IBaseNodeProperties> = (props) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  let {
    plexGetNodeMetadata,
    plexShowOverlay,
    plexSearchStandardObjects,
    plexSearchCustomObjects,
    plexSearchPlexEvents,
    plexSearchPlexActions,
    plexSearchPlexApplicationActions,
    plexSearchExternalApiEndpoint,
    plexSearchPlexOpenApi,
    plexSearchPlexAgents
  } = useFunctionSubscriber();
  const viewController = useViewController();
  const node = reactFlowInstance.getNode(props.id);
  const [nodeMetaResult, setNodeMetaResult] = useState<{ nodeMeta: INodeMetadata | undefined }>({
    nodeMeta: undefined
  });

  const defaultSectionName = "Details";

  const updateNode = () => {
    forceNodeUpdate(node, updateNodeInternals, reactFlowInstance);
  };

  const generateFormSections = () => {
    let nodeTypeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node!.type!)!;

    if (!nodeTypeDefinition.nodeConfigProperties) {
      return <div>No Configurable Properties</div>;
    }

    let formSections: JSX.Element[] = [];
    let formRowsForDefaultSection: JSX.Element[] = [];
    const groupedFormSectionRows: { [name: string]: JSX.Element[] } = {};

    formRowsForDefaultSection.push(
      <Form.Row key={`${defaultSectionName} Name`}>
        <Form.FieldPair labelText="Name" key="Name">
          <TextFormField
            name="name"
            sectionName={defaultSectionName}
            label="Name"
            node={node!}
            characterLength={25}
            forceNodeUpdate={updateNode}
          />
        </Form.FieldPair>
      </Form.Row>
    );

    nodeTypeDefinition.nodeConfigProperties.forEach((nodeConfigProperty, nodeConfigPropertyIndex) => {
      // Create sections, either grouped by section name or ungrouped.
      // Some property types don't support grouping due to special section styling requirements.
      const showRule = nodeConfigProperty.showOnPropertyValues;

      let propertyIsShowing = true;
      if (showRule) {
        const nodeValue = node!.data.nodeProperties[showRule.propertyName];

        // Normalize nodeValue to array for easier comparison
        const nodeValues = Array.isArray(nodeValue) ? nodeValue : [nodeValue];

        if (!Array.isArray(nodeValue) || showRule.listExactMatch) {
          // Show if value matches
          propertyIsShowing = showRule.propertyValues.some(
            (value: any) => value === node!.data.nodeProperties[showRule.propertyName]
          );
        } else {
          // Show if any value is in array
          propertyIsShowing = showRule.propertyValues.some((value: any) => nodeValues.includes(value));
        }
      }

      if (propertyIsShowing) {
        if (nodeConfigProperty.sectionName) {
          const ungroupedSection = createUngroupedNodePropertyFormSection(nodeConfigProperty, nodeConfigPropertyIndex);
          if (ungroupedSection) {
            formSections.push(ungroupedSection);
          } else {
            if (!groupedFormSectionRows[nodeConfigProperty.sectionName]) {
              groupedFormSectionRows[nodeConfigProperty.sectionName] = [];
            }
            groupedFormSectionRows[nodeConfigProperty.sectionName]!.push(createNodePropertyFormRow(nodeConfigProperty));
          }
        } else {
          formRowsForDefaultSection.push(createNodePropertyFormRow(nodeConfigProperty));
        }
      }
    });

    formSections.unshift(
      <Form.Section title={defaultSectionName} key={defaultSectionName}>
        {formRowsForDefaultSection}
      </Form.Section>
    );

    // Create sections by groups, to keep rows under the same group section name.
    Object.keys(groupedFormSectionRows).forEach((groupedSectionName: string) => {
      const groupedSectionRows: JSX.Element[] | undefined = groupedFormSectionRows[groupedSectionName];
      formSections.push(
        <Form.Section title={groupedSectionName} key={groupedSectionName}>
          {groupedSectionRows}
        </Form.Section>
      );
    });

    return formSections;
  };

  const createUngroupedNodePropertyFormSection = (
    nodeConfigProperty: INodeProperty,
    nodeConfigPropertyIndex: number
  ) => {
    let formSection: any = null;

    switch (nodeConfigProperty.propertyType) {
      case NodeConfigPropertyType.EnabledInputs:
        formSection = (
          <EnableInputsFormSection
            // Can't have duplicate keys in React. Need this workaround to have multiple
            // EnableInputsFormSections on the same node config dialog.
            key={`EnableInputsFormSection${nodeConfigPropertyIndex}`}
            name={nodeConfigProperty.name}
            sectionName={nodeConfigProperty.sectionName ?? "Input"}
            node={node!}
            forceNodeUpdate={updateNode}
            onEdgeDelete={viewController.validateEdges}
          />
        );
        break;
      case NodeConfigPropertyType.EnabledOutputs:
        formSection = (
          <EnableOutputsFormSection
            key="EnableOutputsFormSection"
            name={nodeConfigProperty.name}
            sectionName={nodeConfigProperty.sectionName ?? "Output"}
            node={node!}
            forceNodeUpdate={updateNode}
            onEdgeDelete={viewController.validateEdges}
          />
        );
        break;
      case NodeConfigPropertyType.DataOutputs:
        formSection = (
          <FlowInputFormSection
            key="FlowInputFormSection"
            name={nodeConfigProperty.name}
            sectionName={nodeConfigProperty.sectionName ?? "Inputs"}
            viewController={viewController}
            node={node!}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.DataInputs:
        formSection = (
          <FlowOutputFormSection
            key="FlowOutputFormSection"
            name={nodeConfigProperty.name}
            sectionName={nodeConfigProperty.sectionName ?? "Flow Output"}
            node={node!}
            forceNodeUpdate={updateNode}
            onEdgeDelete={viewController.validateEdges}
          />
        );
        break;
    }

    return formSection;
  };

  //HACK: Use fake record to test object references.
  const getFakeObjectReferenceSo = () => {
    return {
      id: "fakeSoWithReference",
      name: "ObjectWithReference"
    };
  };
  const standardObjectSearch = (query: string, customFieldsOnly: boolean) => {
    if (plexSearchStandardObjects) {
      return plexSearchStandardObjects(query, customFieldsOnly).then((result: any) => {
        const resultData = convertKeysToCamelCaseArray(result.Data);
        const results: SourceAPI[] = resultData.map((obj: any) => {
          return {
            id: obj.id,
            name: obj.friendlyName
          };
        });

        if (viewController.experimentalModeState) {
          //HACK: Use fake record to test object references.
          results.push(getFakeObjectReferenceSo());
        }

        return results;
      });
    }

    return Promise.resolve([]);
  };

  const customObjectSearch = (query: string) => {
    if (plexSearchCustomObjects) {
      return plexSearchCustomObjects(query, true).then((result: any) => {
        const resultData = convertKeysToCamelCaseArray(result.Data);
        const results: SourceAPI[] = resultData.map((obj: any) => {
          return {
            id: obj.id,
            name: obj.name
          };
        });

        return results;
      });
    } else {
      console.warn("Custom object search metadata endpoint does not exist.");
    }

    return Promise.resolve([]);
  };

  const plexActionSearch = (eventTopicId: string, actionTitle: string | null) => {
    if (plexSearchPlexApplicationActions) {
      return plexSearchPlexApplicationActions(eventTopicId, actionTitle).then((result: any) => {
        const results: IActionEventPickerRow[] = result.Data.map((obj: any) => ({
          id: obj.Id,
          name: obj.ActionName, // primary display
          applicationName: obj.ApplicationName,
          applicationActionName: obj.ActionName
        }));

        return results;
      });
    } else if (plexSearchPlexActions) {
      return plexSearchPlexActions(eventTopicId, actionTitle).then((result: any) => {
        const results: SourceAPI[] = result.Data.map((obj: any) => {
          return {
            id: obj.Id,
            name: obj.Name
          };
        });

        return results;
      });
    } else {
      console.warn("Action search metadata endpoint does not exist.");
    }

    return Promise.resolve([]);
  };

  const plexEventSearch = (query: string, tags: string[]) => {
    if (plexSearchPlexEvents) {
      return plexSearchPlexEvents(query, tags).then((result: any) => {
        const results: SourceAPI[] = result.Data.map((obj: any) => {
          return {
            id: obj.Id,
            name: obj.Name
          };
        });

        return results;
      });
    } else {
      console.warn("Event search metadata endpoint does not exist.");
    }

    return Promise.resolve([]);
  };

  const plexExternalApiEndpointSearch = (endpointGroupId: string | null, endpointName: string | null) => {
    if (plexSearchExternalApiEndpoint) {
      return plexSearchExternalApiEndpoint(endpointGroupId, endpointName).then((result: any) => {
        const results: IExternalApi[] = result.Data.map((obj: any) => {
          return {
            id: obj.EndpointId,
            name: obj.EndpointName,
            endpointGroupId: obj.EndpointGroupId,
            endpointGroupName: obj.EndpointGroupName
          };
        });

        return results;
      });
    } else {
      console.warn("External API endpoints search metadata endpoint does not exist.");
    }

    return Promise.resolve([]);
  };

  const checkOutputsAgainstSchema = (
    schema: IDesignerSchema | undefined,
    outputName: string | undefined = undefined
  ) => {
    return new Promise<void>((resolve) => {
      let outputEdges = reactFlowInstance.getEdges().filter((e) => e.target === node!.id);
      if (outputName) {
        outputEdges = outputEdges.filter(
          (e: Edge<any>) => e.targetHandle && getNameFromDataHandleId(e.targetHandle) === outputName
        );
      }
      const outputEdgeSchemas: INewEdgeSchema[] = outputEdges.map((edge: Edge) => {
        return { edge: edge, newSchema: schema };
      });
      checkAndRemoveUnmappable(outputEdgeSchemas, () => resolve(), reactFlowInstance, updateNodeInternals);
    });
  };

  const checkNodeMetaSchemaChanges = (
    node: Node<any>,
    nodeConfigProperty: INodeProperty,
    getSchema: (id: string) => Promise<INodeMetadata | undefined>
  ) => {
    return nodeConfigProperty.schemaSystem?.createIo
      ? (newValues) => {
          if (!newValues || newValues.length === 0) {
            setNodeMetaResult({ nodeMeta: undefined });
            nodeMetaResult.nodeMeta = undefined;
            return checkOutputsAgainstSchema(undefined);
          }

          return new Promise<void>((resolve) => {
            plexShowOverlay && plexShowOverlay(true);
            getSchema(newValues[0].id).then((result: INodeMetadata | undefined) => {
              plexShowOverlay && plexShowOverlay(false);
              setNodeMetaResult({ nodeMeta: result });
              if (result) {
                nodeMetaResult.nodeMeta = result;
                const schemaSystem = nodeConfigProperty.schemaSourceSystemType as string;
                const schemaSystemId = node.data.nodeProperties[nodeConfigProperty.name] as string;
                addSchemas(result.schemas, schemaSystem, schemaSystemId);
                if (result.outputs.length > 0) {
                  const promises: Promise<void>[] = [];
                  result.outputs.forEach((resultOutput: IDocumentDataOutput) => {
                    const outputSchema = result.schemas.find((s: IDocumentSchema) => s.id === resultOutput?.schema);
                    promises.push(checkOutputsAgainstSchema(outputSchema as IDesignerSchema, resultOutput.name));
                  });

                  Promise.all(promises).then(() => resolve());
                } else resolve();
              }
            });
          });
        }
      : () => Promise.resolve();
  };

  const loadSchemaFromMetaResult = (nodeConfigProperty: INodeProperty, resetWhenEmpty: boolean = true) => {
    if (nodeConfigProperty.schemaSystem?.createIo && nodeMetaResult.nodeMeta) {
      return loadNodeMetadata(node!, nodeMetaResult.nodeMeta!, nodeConfigProperty, updateNodeInternals, reactFlowInstance);
    }
    // Some pickers' inputs are client-owned (declared statically on the node type, e.g. the Plex
    // Agent node's instructions/inputData) rather than driven by the picked value, so those callers
    // pass resetWhenEmpty=false to skip resetIo entirely when the picker is cleared.
    if (resetWhenEmpty) {
      return resetIo(node!, reactFlowInstance, updateNodeInternals);
    }
  };

  const getNodeIoQuery = (getObjectIdData: (objectId: string) => {}) => {
    return (objectId: string) => {
      return new Promise<any>((resolve, reject) => {
        if (plexGetNodeMetadata) {
          plexGetNodeMetadata(camelToUpperSnakeCase(node!.type!), getObjectIdData(objectId))
            .then((response) => {
              if (response.ValidationResult?.Success === true) {
                const metadataJsonString = response?.Data?.NodeMetadata;
                const metadata = JSON.parse(metadataJsonString);
                resolve(metadata);
              } else {
                viewController.bannerContext.addMessage("Failed to load node metadata.", BannerStatus.error);
                reject(response);
              }
            })
            .catch((error) => {
              reject(error);
            });
        } else {
          console.warn("Node metadata endpoint does not exist.");
        }
      });
    };
  };

  const getEventTags = () => {
    let eventTags: string[];
    if (node?.type === NodeType.eventTrigger) {
      eventTags = ["type:domain"];
    } else if (node?.type === NodeType.actionEventTrigger) {
      eventTags = ["type:ui"];
    } else {
      eventTags = [];
    }
    return eventTags;
  };

  const createNodePropertyFormRow = (nodeConfigProperty: INodeProperty) => {
    const formFields: ReactElement[] = [];
    const sectionName = nodeConfigProperty.sectionName ?? defaultSectionName;
    const label = nodeConfigProperty.label ?? node!.type!;

    switch (nodeConfigProperty.propertyType) {
      case NodeConfigPropertyType.Text:
        formFields.push(
          <TextFormField
            key={`${sectionName} ${label} Text`}
            name={nodeConfigProperty.name}
            sectionName={sectionName}
            label={label}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
          />
        );
        break;
      case NodeConfigPropertyType.TemplateEditor:
        formFields.push(
          <TemplateEditorField
            key={`${sectionName} ${label} TemplateEditor`}
            name={nodeConfigProperty.name}
            sectionName={sectionName}
            label={label}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
          />
        );
        break;
      case NodeConfigPropertyType.Boolean:
        formFields.push(
          <CheckboxFormField
            key={`${sectionName} ${label} Boolean`}
            name={nodeConfigProperty.name}
            sectionName={sectionName}
            label={label}
            node={node!}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.Integer:
        formFields.push(
          <NumericFormField
            key={`${sectionName} ${label} Number`}
            name={nodeConfigProperty.name}
            sectionName={sectionName}
            label={label}
            node={node!}
            forceNodeUpdate={updateNode}
            numericOptions={{ scale: 0 }}
          />
        );
        break;
      case NodeConfigPropertyType.Decimal:
        formFields.push(
          <NumericFormField
            key={`${sectionName} ${label} Number`}
            name={nodeConfigProperty.name}
            sectionName={sectionName}
            label={label}
            node={node!}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.FlowInputDataEditor:
        const inputEditorOptions = nodeConfigProperty.options as IFlowInputEditorOptions;
        const buttonlabel = inputEditorOptions.label ?? camelCaseToReadable(inputEditorOptions.inputSubName);
        formFields.push(
          <Button
            data-testid={nodeConfigProperty.name}
            style={{ margin: "10px" }}
            key={`${sectionName} ${buttonlabel}`}
            onClick={() =>
              viewController.setFlowInputDataEditorState({
                ...viewController.flowInputDataEditorState,
                show: true,
                title: buttonlabel,
                inputName: getFlowInputName(node!.id, inputEditorOptions.inputSubName),
                excludedProperties: inputEditorOptions.excludedProperties
              })
            }
          >
            {buttonlabel}
          </Button>
        );
        break;
      case NodeConfigPropertyType.DataSource:
        if (node?.type === NodeType.callDataSource) {
          formFields.push(
            <DataSourcePickerFormField
              key={`${sectionName} Data Source Picker`}
              name={nodeConfigProperty.name}
              node={node!}
              forceNodeUpdate={updateNode}
            />
          );
        } else if (node?.type === NodeType.listSort) {
          formFields.push(
            <DataInputFormField
              key={`${sectionName} Data Input`}
              name={nodeConfigProperty.name}
              sectionName={sectionName}
              label={label}
              dialogTitle={label}
              node={node!}
              forceNodeUpdate={updateNode}
              typesAllowed={["string", "integer", "decimal", "boolean", "date", "dateTime", "time"]}
              searchColumns={[
                { id: "DataSourceName", title: "Name", valueSelector: (row: IDataParam) => row.name },
                { id: "DataSourceType", title: "Type", valueSelector: (row: IDataParam) => row.type }
              ]}
            />
          );
        }
        break;
      case NodeConfigPropertyType.DataPropertyCount:
        formFields.push(
          <DataPropertyCountFormField
            key={`${sectionName} Data Property Count`}
            name={nodeConfigProperty.name}
            node={node!}
            options={nodeConfigProperty.options}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.DataType:
        if (node?.type === NodeType.stop) {
          formFields.push(
            <StopTypeFormField
              key={`${sectionName} Stop Type`}
              name={nodeConfigProperty.name}
              node={node}
              forceNodeUpdate={updateNode}
            />
          );
        } else {
          const options = nodeConfigProperty.options as IDataTypeOptions;
          let dataTypes = primitiveTypesSelect;
          if (options?.dataTypes) {
            dataTypes = options.dataTypes.map((typeName: string) => {
              return { key: typeName, value: typeName };
            });
          }
          formFields.push(
            <DataTypeFormField
              key={`${sectionName} Value Type`}
              name={nodeConfigProperty.name}
              node={node!}
              forceNodeUpdate={updateNode}
              dataTypes={dataTypes}
              properties={options.dataProperties}
              clearPropertiesOnChange={nodeConfigProperty.clearPropertiesOnChange}
            />
          );
        }
        break;
      case NodeConfigPropertyType.DataValue:
        const inputBasisType = "outputType";
        const dataValueFormField: any = ValueFormField({
          name: nodeConfigProperty.name,
          inputBasisType: inputBasisType,
          node: node!,
          forceNodeUpdate: updateNode
        });

        if (dataValueFormField) {
          formFields.push(dataValueFormField!);
        }
        break;
      case NodeConfigPropertyType.Enum:
        const enumFormField: any = EnumFormField({
          name: nodeConfigProperty.name,
          node: node!,
          forceNodeUpdate: updateNode,
          options: nodeConfigProperty.options
        });
        if (enumFormField) {
          formFields.push(enumFormField!);
        }
        break;
      case NodeConfigPropertyType.Position:
        formFields.push(
          <ItemPositionFormField
            key={`${sectionName} Item Position`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.ViewDataSourceDetails:
        formFields.push(
          <ViewDataSourceDetailsField
            key={`${sectionName}`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.Schema:
        formFields.push(
          <SchemaPickerField
            key={`${sectionName}`}
            dialogTitle="Schema"
            displayName="schemaName"
            name={nodeConfigProperty.name}
            options={nodeConfigProperty.options}
            node={node!}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.SchemaProperty:
        formFields.push(
          <SchemaPropertyPickerField
            key={`${sectionName}`}
            dialogTitle="Schema"
            displayName="schemaName"
            name={nodeConfigProperty.name}
            options={nodeConfigProperty.options}
            node={node!}
            forceNodeUpdate={updateNode}
          />
        );
        break;
      case NodeConfigPropertyType.StandardObject:
        // TODO: Remove experimental check on release CORE-2372
        const standardObjectOptions = nodeConfigProperty.options as IStandardObjectPickerOptions;
        const soIoQuery = getNodeIoQuery((objectId: string) => {
          return { id: objectId };
        });
        formFields.push(
          <SourceApiPickerFormField
            dialogTitle="Standard Object"
            displayName="standardObjectName"
            searchFunction={(query) => standardObjectSearch(query, standardObjectOptions.customFieldsOnly)}
            multiSelect={standardObjectOptions.multiSelect}
            key={`${sectionName} StandardObject Picker`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
            beforeChange={checkNodeMetaSchemaChanges(node!, nodeConfigProperty, soIoQuery)}
            onChange={() => loadSchemaFromMetaResult(nodeConfigProperty)!}
          />
        );
        break;
      case NodeConfigPropertyType.CustomObject:
        const customObjectOptions = nodeConfigProperty.options as ISourceApiPickerOptions;
        const coIoQuery = getNodeIoQuery((objectId: string) => {
          return { id: objectId };
        });
        formFields.push(
          <SourceApiPickerFormField
            dialogTitle="Custom Object"
            displayName="customObjectName"
            searchFunction={customObjectSearch}
            multiSelect={customObjectOptions?.multiSelect}
            key={`${sectionName} CustomObject Picker`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
            beforeChange={
              coIoQuery !== undefined ? checkNodeMetaSchemaChanges(node!, nodeConfigProperty, coIoQuery) : undefined
            }
            onChange={coIoQuery !== undefined ? () => loadSchemaFromMetaResult(nodeConfigProperty)! : undefined}
          />
        );
        break;

      case NodeConfigPropertyType.Event:
        const eventOptions = nodeConfigProperty.options as IEventPropertyOptions;
        const evIoQuery = getNodeIoQuery((objectId: string) => {
          return { id: objectId };
        });
        const eventTags = getEventTags();
        formFields.push(
          <SourceApiPickerFormField
            dialogTitle="Events"
            displayName="eventTopicName"
            searchFunction={(query) => plexEventSearch(query, eventTags)}
            multiSelect={eventOptions.multiSelect}
            key={`${sectionName} Event Picker`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
            beforeChange={
              evIoQuery !== undefined ? checkNodeMetaSchemaChanges(node!, nodeConfigProperty, evIoQuery) : undefined
            }
            onChange={
              evIoQuery !== undefined
                ? () => {
                    if (eventOptions.clearPropertiesOnChange) {
                      eventOptions.clearPropertiesOnChange.forEach((propertyToClear) => {
                        if (node?.data.nodeProperties[propertyToClear]) {
                          node.data.nodeProperties[propertyToClear] = undefined;
                        }
                      });
                    }

                    loadSchemaFromMetaResult(nodeConfigProperty)!;
                    if (eventOptions.setFlowInputsFromSchemas) {
                      setFlowInputsToEventSchemas(node!, viewController);
                    }
                  }
                : undefined
            }
          />
        );
        break;

      case NodeConfigPropertyType.Actions:
        const actionOptions = nodeConfigProperty.options as IActionEventPropertyOptions;
        formFields.push(
          <SourceApiPickerFormField
            dialogTitle="Actions"
            displayName="actionName"
            columnTitle="Action"
            additionalColumns={[
              {
                id: "applicationName",
                title: "Application",
                valueSelector: (r: IActionEventPickerRow) => r.applicationName
              }
            ]}
            additionalColumnsPlacement="before"
            searchFunction={(query) => {
              if (!actionOptions.eventIdProperty) {
                return Promise.resolve([]);
              }

              const eventTopicId = node?.data.nodeProperties[actionOptions.eventIdProperty];
              const queryString = query === "" ? null : query;

              return Promise.resolve(eventTopicId ? plexActionSearch(eventTopicId, queryString) : []);
            }}
            multiSelect={actionOptions.multiSelect}
            key={`${sectionName} Action Picker`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
          />
        );
        break;

      case NodeConfigPropertyType.PlexOpenApi:
        const poaIoQuery = getNodeIoQuery((objectId: string) => {
          return { id: objectId };
        });
        formFields.push(
          <SourceApiPickerFormField
            dialogTitle="Plex API"
            displayName="plexOpenApiName"
            columnTitle="Operation"
            initialWidth={1200}
            additionalColumns={[
              {
                id: "ApiName",
                title: "API Collection",
                valueSelector: (r: any) => r.apiName,
                searchable: true
              },
              {
                id: "Version",
                title: "Version",
                valueSelector: (r: any) => r.version,
                searchable: false
              },
              {
                id: "OperationType",
                title: "Operation Type",
                valueSelector: (r: any) => r.operationType,
                searchable: false
              }
            ]}
            searchFunction={(query) => {
              if (plexSearchPlexOpenApi) {
                return plexSearchPlexOpenApi(query).then((response: any) =>
                  (response?.Data ?? []).map((obj: any) => ({
                    id: obj.Id,
                    name: obj.Name,
                    apiName: obj.ApiName,
                    version: obj.Version,
                    operationType: obj.OperationType,
                    betaEndDate: obj.BetaEndDate
                  }))
                );
              }
              console.warn("Plex open api search metadata endpoint does not exist.");
              return Promise.resolve([]);
            }}
            key={`${sectionName} Plex Open Api Picker`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
            beforeChange={
              poaIoQuery !== undefined ? checkNodeMetaSchemaChanges(node!, nodeConfigProperty, poaIoQuery) : undefined
            }
            onChange={poaIoQuery !== undefined ? () => loadSchemaFromMetaResult(nodeConfigProperty)! : undefined}
          />
        );

        break;

      case NodeConfigPropertyType.MlModel:
        const mlModelIoQuery = getNodeIoQuery((objectId: string) => {
          return { id: objectId };
        });
        formFields.push(
          <SourceApiPickerFormField
            dialogTitle="ML Model"
            displayName="mlModelName"
            columnTitle="Operation"
            initialWidth={1200}
            additionalColumns={[
              {
                id: "ApiName",
                title: "API Collection",
                valueSelector: (r: any) => r.apiName,
                searchable: true
              },
              {
                id: "Version",
                title: "Version",
                valueSelector: (r: any) => r.version,
                searchable: false
              },
              {
                id: "OperationType",
                title: "Operation Type",
                valueSelector: (r: any) => r.operationType,
                searchable: false
              }
            ]}
            searchFunction={(query) => {
              // HACK todo: Decide what these should be and say while we wait for a registry
              let data;
              data = [];
              data.push({
                id: "POST: /v1/predict",
                name: "Plex Downtime Prediction API (DFP)",
                apiName: "GetDowntimePrediction",
                version: "v0.7",
                operationType: "Post"
              });
              return Promise.resolve(data);
              /*
              if (plexSearchMlModel) {
                return plexSearchMlModel(query).then((response: any) =>
                  (response?.Data ?? []).map((obj: any) => ({
                    id: obj.Id,
                    name: obj.Name,
                    apiName: obj.ApiName,
                    version: obj.Version,
                    operationType: obj.OperationType
                  }))
                );
              }
              console.warn("ML Model search metadata endpoint does not exist.");
              return Promise.resolve([]);
              */
            }}
            key={`${sectionName} Ml Model Picker`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
            beforeChange={
              mlModelIoQuery !== undefined ? checkNodeMetaSchemaChanges(node!, nodeConfigProperty, mlModelIoQuery) : undefined
            }
            onChange={mlModelIoQuery !== undefined ? () => loadSchemaFromMetaResult(nodeConfigProperty)! : undefined}
          />
        );

        break;

      case NodeConfigPropertyType.PlexAgent:
        const plexAgentIoQuery = getNodeIoQuery((objectId: string) => {
          return { id: objectId };
        });
        formFields.push(
          <div>
            <SourceApiPickerFormField
              dialogTitle="Plex Agent"
              displayName="agentName"
              columnTitle="Agent"
              searchFunction={(query) => {
                if (plexSearchPlexAgents) {
                  const queryString = query === "" ? null : query;
                  return plexSearchPlexAgents(queryString).then((response: any) =>
                    (response?.Data ?? []).map((obj: any) => ({ id: obj.Id, name: obj.Name }))
                  );
                }
                console.warn("Plex agent search metadata endpoint does not exist.");
                return Promise.resolve([]);
              }}
              key={`${sectionName} Plex Agent Picker`}
              name={nodeConfigProperty.name}
              node={node!}
              forceNodeUpdate={updateNode}
              options={nodeConfigProperty.options}
              // The Plex Agent node's instructions/inputData inputs are client-owned - they don't
              // depend on which agent is selected - so their existing connections must survive a
              // selection change/clear even though other, schema-driven inputs don't.
              preserveInputConnections={true}
              beforeChange={
                plexAgentIoQuery !== undefined
                  ? checkNodeMetaSchemaChanges(node!, nodeConfigProperty, plexAgentIoQuery)
                  : undefined
              }
              onChange={plexAgentIoQuery !== undefined ? () => loadSchemaFromMetaResult(nodeConfigProperty, false)! : undefined}
            />
            <div data-testid="viewPlexAgentDetailsButton">
              <ViewPlexAgentDetailsButton
                key={`${sectionName} View Plex Agent Details`}
                name={nodeConfigProperty.name}
                node={node!}
                forceNodeUpdate={updateNode}
              />
            </div>
         </div>
        );
        break;

      case NodeConfigPropertyType.ExternalApiEndpoint:
        const exEndpointIoQuery = getNodeIoQuery((objectId: string) => {
          return { id: objectId };
        });
        formFields.push(
          <SourceApiPickerFormField
            dialogTitle="Endpoint"
            displayName="endpointName"
            additionalColumns={[
              {
                id: "endpointGroupName",
                title: "Endpoint Group",
                valueSelector: (r: IExternalApi) => r.endpointGroupName
              }
            ]}
            additionalColumnsPlacement="after"
            searchFunction={(query) => {
              const queryString = query === "" ? null : query;

              return Promise.resolve(plexExternalApiEndpointSearch(null, queryString));
            }}
            key={`${sectionName} Endpoint Picker`}
            name={nodeConfigProperty.name}
            node={node!}
            forceNodeUpdate={updateNode}
            options={nodeConfigProperty.options}
            beforeChange={
              exEndpointIoQuery !== undefined
                ? checkNodeMetaSchemaChanges(node!, nodeConfigProperty, exEndpointIoQuery)
                : undefined
            }
            onChange={
              exEndpointIoQuery !== undefined
                ? () => {
                    loadSchemaFromMetaResult(nodeConfigProperty)!;
                  }
                : undefined
            }
          />
        );
        break;

      case NodeConfigPropertyType.LoopVariableEditor:
        formFields.push(
          <LoopVariableEditor key={`${sectionName} ${label} Loop Variable Editor`} nodeId={node?.id ?? ""} />
        );
        break;

      case NodeConfigPropertyType.NoInput:
      case NodeConfigPropertyType.ConnectedSchemaTags:
        // Items in a list need a key, so div w/ key is required here
        return <div key={`${sectionName} ${label} ${nodeConfigProperty.name}`} />;
    }

    let readableName = nodeConfigProperty.name.replaceAll("_", " ");
    readableName = readableName.charAt(0).toUpperCase() + readableName.slice(1);

    return (
      <Form.Row key={`${sectionName} ${label} ${nodeConfigProperty.name}`}>
        <div key={`${label} Div`} data-testid={`${nodeConfigProperty.name}Property`}>
          <Form.FieldPair key={label} labelText={nodeConfigProperty.label ?? readableName}>
            {formFields}
          </Form.FieldPair>
        </div>
      </Form.Row>
    );
  };

  return (
    <div>
      <Form
        className="node-properties-form"
        onSubmit={(e: any) => e.preventDefault()}
        style={{ padding: "0px 0px 4px" }}
      >
        {generateFormSections()}
      </Form>
    </div>
  );
};
